import { useState } from "react";
import { Star, X, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { api } from "../lib/api";

const roleLabel: Record<string, string> = { seller: "as a seller", buyer: "as a buyer", provider: "as a provider", client: "as a client" };

/** Read-only star row. */
export function Stars({ value, size = 16 }: { value: number; size?: number }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${value} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} style={{ width: size, height: size }}
          className={n <= Math.round(value) ? "fill-brand-gold text-brand-gold" : "text-line"} />
      ))}
    </span>
  );
}

/** Interactive 1–5 picker. */
export function StarPicker({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => onChange(n)}
          className="p-0.5" aria-label={`${n} star${n === 1 ? "" : "s"}`}>
          <Star className={`w-7 h-7 ${n <= (hover || value) ? "fill-brand-gold text-brand-gold" : "text-line"}`} />
        </button>
      ))}
    </div>
  );
}

/** Aggregate summary: big average + count + histogram. */
export function RatingSummary({ average, count, breakdown }: { average: number; count: number; breakdown?: Record<string, number> }) {
  if (!count) return <p className="text-sm text-mut">No reviews yet.</p>;
  return (
    <div className="flex items-start gap-6">
      <div className="text-center">
        <div className="text-4xl font-extrabold text-navy leading-none">{average.toFixed(1)}</div>
        <div className="mt-1"><Stars value={average} /></div>
        <div className="text-xs text-mut mt-1">{count} review{count === 1 ? "" : "s"}</div>
      </div>
      {breakdown && (
        <div className="flex-1 space-y-1 pt-1">
          {[5, 4, 3, 2, 1].map((n) => {
            const c = breakdown[String(n)] || 0;
            const pct = count ? Math.round((c / count) * 100) : 0;
            return (
              <div key={n} className="flex items-center gap-2 text-xs text-mut">
                <span className="w-3 text-right">{n}</span>
                <Star className="w-3 h-3 fill-brand-gold text-brand-gold" />
                <div className="flex-1 h-2 rounded bg-navy-50 overflow-hidden"><div className="h-full bg-brand-gold" style={{ width: `${pct}%` }} /></div>
                <span className="w-6 text-right">{c}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** A list of review cards. */
export function ReviewList({ reviews, emptyText = "No reviews yet." }: { reviews: any[]; emptyText?: string }) {
  if (!reviews?.length) return <p className="text-sm text-mut">{emptyText}</p>;
  return (
    <div className="space-y-3">
      {reviews.map((r) => (
        <div key={r.id} className="border border-line rounded-lg p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 rounded-full bg-navy-50 grid place-items-center text-navy font-bold text-xs shrink-0">{(r.raterName || "M").slice(0, 1)}</div>
              <span className="text-sm font-semibold text-navy truncate">{r.raterName || "Member"}</span>
              <span className="text-xs text-mut hidden sm:inline">{roleLabel[r.rateeRole] || ""}</span>
            </div>
            <Stars value={r.rating} size={14} />
          </div>
          {r.comment && <p className="text-sm text-navy-700 mt-2">{r.comment}</p>}
          {r.contextLabel && <p className="text-xs text-mut mt-1">on “{r.contextLabel}”</p>}
        </div>
      ))}
    </div>
  );
}

/** Modal review form for a pending deal. Calls onDone(review) on success. */
export function ReviewForm({ deal, onClose, onDone }: {
  deal: { dealType: "order" | "contract"; dealId: string; counterpartyName?: string; contextLabel?: string; counterpartyRole?: string };
  onClose: () => void; onDone: (review: any) => void;
}) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit() {
    setErr(null);
    if (rating < 1) { setErr("Pick a star rating."); return; }
    setBusy(true);
    try {
      const res = await api.submitReview({ dealType: deal.dealType, dealId: deal.dealId, rating, comment: comment.trim() || undefined });
      if (res?.review) { setDone(true); setTimeout(() => onDone(res.review), 700); }
      else setErr("Could not submit the review.");
    } catch (e: any) { setErr(e?.message || "Could not submit the review."); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/50 backdrop-blur-sm" onClick={onClose}>
      <div className="card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-extrabold text-navy">Leave a review</h3>
          <button onClick={onClose} className="btn-ghost h-8 w-8 p-0"><X className="w-5 h-5" /></button>
        </div>
        {done ? (
          <div className="text-center py-6">
            <CheckCircle2 className="w-10 h-10 text-brand-green mx-auto mb-2" />
            <div className="font-bold text-navy">Thanks for your review!</div>
          </div>
        ) : (
          <>
            <p className="text-sm text-mut mb-3">
              How was your experience with <span className="font-semibold text-navy">{deal.counterpartyName || "your counterparty"}</span>
              {deal.contextLabel ? <> on “{deal.contextLabel}”</> : null}?
            </p>
            <div className="mb-3"><StarPicker value={rating} onChange={setRating} /></div>
            <textarea className="field h-24 py-2" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Share the details of your experience (optional)…" maxLength={1000} />
            {err && <p className="text-sm text-brand-red flex items-center gap-1 mt-2"><AlertCircle className="w-4 h-4" />{err}</p>}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={onClose} className="btn-outline">Cancel</button>
              <button onClick={submit} disabled={busy} className="btn-red disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />}Submit review</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
